import type { ContextMenuItem } from "@t3tools/contracts";
import { LegendList, type LegendListRef } from "@legendapp/list/react";
import { ChevronDownIcon, GitBranchIcon, RefreshCwIcon, SearchIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";

import { writeTextToClipboard } from "../hooks/useCopyToClipboard";
import { readLocalApi } from "../localApi";
import { useOpenPrLink } from "../lib/openPullRequestLink";
import { cn } from "../lib/utils";
import { shouldLoadNextBranchPageAfterScroll } from "../state/paginatedBranches";
import { useRunContextController } from "./RunContextController";
import { ChangeRequestStatusIcon } from "./ThreadStatusIndicators";
import { Button } from "./ui/button";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxListVirtualized,
  ComboboxPopup,
  ComboboxStatus,
  ComboboxTrigger,
} from "./ui/combobox";
import { Switch } from "./ui/switch";
import { stackedThreadToast, toastManager } from "./ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

function toBranchActionErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "An error occurred.";
}

interface BranchToolbarBranchSelectorProps {
  readonly className?: string;
  readonly menuOwner?: string;
  readonly presentation?: "toolbar" | "summary";
  readonly popupAlign?: "start" | "center" | "end";
  readonly popupSide?: "top" | "bottom" | "left" | "right";
  readonly popupSideOffset?: number;
}

export function BranchToolbarBranchSelector({
  className,
  menuOwner = "composer",
  presentation = "toolbar",
  popupAlign = "end",
  popupSide = "top",
  popupSideOffset = 4,
}: BranchToolbarBranchSelectorProps) {
  const controller = useRunContextController();
  const branch = controller.branch;
  const isMenuOpen = branch.isMenuOpen && branch.menuOwner === menuOwner;
  const SourceControlIcon = branch.sourceControlPresentation.Icon;
  const openPrLink = useOpenPrLink();
  const releaseMenuOwner = useEffectEvent((owner: string) => {
    branch.setMenuOpen(owner, false);
  });

  useEffect(
    () => () => {
      releaseMenuOwner(menuOwner);
    },
    [menuOwner],
  );

  const copyBranchName = useCallback((branchName: string) => {
    void writeTextToClipboard(branchName, "branch name").then(
      (didCopy) => {
        if (!didCopy) return;
        toastManager.add({
          type: "success",
          title: "Branch name copied",
          description: branchName,
        });
      },
      (error: unknown) => {
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Failed to copy branch name",
            description: toBranchActionErrorMessage(error),
          }),
        );
      },
    );
  }, []);
  const handleBranchContextMenu = useCallback(
    (event: ReactMouseEvent, branchName: string | null) => {
      if (!branchName) return;
      const api = readLocalApi();
      if (!api) return;
      event.preventDefault();
      event.stopPropagation();
      const items: ContextMenuItem<"copy-branch-name">[] = [
        { id: "copy-branch-name", label: "Copy branch name", icon: "copy" },
      ];
      void api.contextMenu.show(items, { x: event.clientX, y: event.clientY }).then((action) => {
        if (action === "copy-branch-name") copyBranchName(branchName);
      });
    },
    [copyBranchName],
  );

  const branchListScrollElementRef = useRef<HTMLElement | null>(null);
  const previousBranchListScrollTopRef = useRef<number | null>(null);
  const [showTopBranchScrollFade, setShowTopBranchScrollFade] = useState(false);
  const [showBottomBranchScrollFade, setShowBottomBranchScrollFade] = useState(false);
  const fetchNextBranchPage = useCallback(() => {
    if (!branch.hasNextPage || branch.isFetchingNextPage) return;
    branch.loadNextPage();
  }, [branch.hasNextPage, branch.isFetchingNextPage, branch.loadNextPage]);
  const maybeFetchNextBranchPage = useCallback(() => {
    const scrollElement = branchListScrollElementRef.current;
    if (!scrollElement) return;
    const previousScrollTop = previousBranchListScrollTopRef.current;
    previousBranchListScrollTopRef.current = scrollElement.scrollTop;
    if (
      !isMenuOpen ||
      !branch.hasNextPage ||
      branch.isFetchingNextPage ||
      !shouldLoadNextBranchPageAfterScroll({
        previousScrollTop,
        scrollTop: scrollElement.scrollTop,
        scrollHeight: scrollElement.scrollHeight,
        clientHeight: scrollElement.clientHeight,
      })
    ) {
      return;
    }
    fetchNextBranchPage();
  }, [branch.hasNextPage, branch.isFetchingNextPage, fetchNextBranchPage, isMenuOpen]);
  const branchListRef = useRef<LegendListRef | null>(null);
  const updateBranchListScrollFades = useCallback(() => {
    const scrollElement = branchListRef.current?.getScrollableNode?.();
    if (!(scrollElement instanceof HTMLElement)) return;
    branchListScrollElementRef.current = scrollElement;
    const maxScrollOffset = Math.max(0, scrollElement.scrollHeight - scrollElement.clientHeight);
    setShowTopBranchScrollFade(scrollElement.scrollTop > 1);
    setShowBottomBranchScrollFade(maxScrollOffset - scrollElement.scrollTop > 1);
  }, []);

  useLayoutEffect(() => {
    if (!isMenuOpen) return;
    setShowTopBranchScrollFade(false);
    setShowBottomBranchScrollFade(branch.filteredBranchPickerItems.length > 8);
    let nestedFrame = 0;
    const frame = requestAnimationFrame(() => {
      updateBranchListScrollFades();
      nestedFrame = requestAnimationFrame(updateBranchListScrollFades);
    });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(nestedFrame);
    };
  }, [
    branch.filteredBranchPickerItems.length,
    isMenuOpen,
    branch.branchQuery,
    updateBranchListScrollFades,
  ]);
  useEffect(() => {
    if (!isMenuOpen) return;
    void branchListRef.current?.scrollToOffset?.({ offset: 0, animated: false });
  }, [branch.branchQuery, isMenuOpen]);

  function renderPickerItem(itemValue: string, index: number) {
    if (branch.checkoutPullRequestItemValue && itemValue === branch.checkoutPullRequestItemValue) {
      return (
        <ComboboxItem
          hideIndicator
          key={itemValue}
          index={index}
          value={itemValue}
          className="pe-2"
          onClick={branch.requestCheckoutPullRequest}
        >
          <div className="flex min-w-0 items-center gap-2 py-1">
            <SourceControlIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="flex min-w-0 flex-col items-start">
              <span className="truncate font-medium">
                Checkout {branch.sourceControlPresentation.terminology.singular}
              </span>
              <span className="truncate text-muted-foreground text-xs">{branch.prReference}</span>
            </span>
          </div>
        </ComboboxItem>
      );
    }
    if (branch.createBranchItemValue && itemValue === branch.createBranchItemValue) {
      return (
        <ComboboxItem
          hideIndicator
          key={itemValue}
          index={index}
          value={itemValue}
          className="pe-1.5"
          onClick={() => branch.createRef(branch.branchQuery)}
        >
          <span className="truncate">Create new ref &quot;{branch.branchQuery.trim()}&quot;</span>
        </ComboboxItem>
      );
    }
    const refName = branch.branchByName.get(itemValue);
    if (!refName) return null;
    const hasSecondaryWorktree =
      refName.worktreePath &&
      branch.activeProjectCwd &&
      refName.worktreePath !== branch.activeProjectCwd;
    const badge = refName.current
      ? "current"
      : hasSecondaryWorktree
        ? "worktree"
        : refName.isRemote
          ? "remote"
          : refName.isDefault
            ? "default"
            : null;
    return (
      <ComboboxItem
        hideIndicator
        key={itemValue}
        index={index}
        value={itemValue}
        className="pe-1.5"
        onClick={() => branch.selectRef(refName)}
        onContextMenu={(event) => handleBranchContextMenu(event, itemValue)}
      >
        <div className="flex w-full min-w-0 items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate">{itemValue}</span>
          {badge && <span className="shrink-0 text-[10px] text-muted-foreground/45">{badge}</span>}
        </div>
      </ComboboxItem>
    );
  }

  return (
    <Combobox
      items={branch.branchPickerItems}
      filteredItems={branch.filteredBranchPickerItems}
      autoHighlight
      virtualized
      onItemHighlighted={(_value, eventDetails) => {
        if (!isMenuOpen || eventDetails.index < 0 || eventDetails.reason !== "keyboard") {
          return;
        }
        void branchListRef.current?.scrollIndexIntoView?.({
          index: eventDetails.index,
          animated: false,
        });
      }}
      onOpenChange={(open) => {
        previousBranchListScrollTopRef.current = null;
        branch.setMenuOpen(menuOwner, open);
      }}
      open={isMenuOpen}
      value={branch.resolvedActiveBranch}
    >
      <div
        className={cn(
          "flex min-w-0 items-center gap-1",
          presentation === "summary" && "w-full",
          className,
        )}
      >
        {branch.branchPr && branch.branchPrStatus ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={branch.branchPrTooltip}
                  onClick={(event) => openPrLink(event, branch.branchPrStatus!.url)}
                  className={cn(
                    "inline-flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-[11px] font-medium tabular-nums transition-colors hover:bg-muted/60",
                    branch.branchPrStatus.colorClass,
                  )}
                />
              }
            >
              <ChangeRequestStatusIcon className="size-3" />
              <span>#{branch.branchPr.number}</span>
            </TooltipTrigger>
            <TooltipPopup side="top">{branch.branchPrTooltip}</TooltipPopup>
          </Tooltip>
        ) : null}
        <span
          className={cn("flex min-w-0", presentation === "summary" && "flex-1")}
          onContextMenu={(event) => handleBranchContextMenu(event, branch.resolvedActiveBranch)}
        >
          <ComboboxTrigger
            render={<Button variant="ghost" size={presentation === "summary" ? "sm" : "xs"} />}
            className={cn(
              "min-w-0 max-w-full",
              presentation === "summary"
                ? "h-10 w-full justify-start gap-3 rounded-lg px-3 text-sm font-medium text-foreground/90 hover:bg-accent/70 hover:text-foreground"
                : "text-muted-foreground/70 hover:text-foreground/80",
            )}
            disabled={branch.isInitialLoadPending || branch.isActionPending}
            aria-label={`Branch: ${branch.triggerLabel}`}
            title={branch.triggerLabel}
          >
            <GitBranchIcon
              className={cn(
                "shrink-0",
                presentation === "summary" ? "size-4 text-foreground/80" : "size-3 opacity-70",
              )}
            />
            <span className="min-w-0 flex-1 truncate text-left">{branch.triggerLabel}</span>
            <ChevronDownIcon
              className={cn(
                "shrink-0 opacity-50",
                presentation === "summary" ? "size-4" : "size-3",
              )}
            />
          </ComboboxTrigger>
        </span>
      </div>
      <ComboboxPopup
        align={popupAlign}
        side={popupSide}
        sideOffset={popupSideOffset}
        className="flex w-80 flex-col"
      >
        <div className="shrink-0 px-3 pt-2.5">
          <div className="relative -translate-y-px border-b border-border/70 pb-1.5 transition-colors focus-within:border-ring">
            <SearchIcon
              aria-hidden="true"
              className="pointer-events-none absolute top-1.5 left-0 size-4 shrink-0 text-muted-foreground/55"
            />
            <ComboboxInput
              className="[&_input]:h-6.5 [&_input]:ps-5 [&_input]:font-sans [&_input]:leading-6.5"
              inputClassName="rounded-none bg-transparent text-sm"
              placeholder="Search refs..."
              showTrigger={false}
              size="sm"
              unstyled
              value={branch.branchQuery}
              onChange={(event) => branch.setBranchQuery(event.target.value)}
            />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ComboboxEmpty>No refs found.</ComboboxEmpty>
          <div className="relative min-h-0 w-full max-h-56 flex-1 overflow-hidden">
            <ComboboxListVirtualized className="size-full min-w-0 p-0">
              <LegendList<string>
                ref={branchListRef}
                data={branch.filteredBranchPickerItems}
                keyExtractor={(item) => item}
                getItemType={(item) =>
                  item === branch.checkoutPullRequestItemValue
                    ? "checkout-pull-request"
                    : item === branch.createBranchItemValue
                      ? "create-branch"
                      : "branch"
                }
                renderItem={({ item, index }) => renderPickerItem(item, index)}
                estimatedItemSize={28}
                drawDistance={336}
                onLayout={() => {
                  updateBranchListScrollFades();
                  previousBranchListScrollTopRef.current =
                    branchListScrollElementRef.current?.scrollTop ?? null;
                }}
                onScroll={() => {
                  updateBranchListScrollFades();
                  maybeFetchNextBranchPage();
                }}
                className={cn(
                  "scrollbar-gutter-stable overflow-x-hidden overscroll-y-contain ps-1 pe-0 pt-2 pb-1 [--fade-size:1.5rem]",
                  showTopBranchScrollFade && "mask-t-from-[calc(100%-var(--fade-size))]",
                  showBottomBranchScrollFade && "mask-b-from-[calc(100%-var(--fade-size))]",
                )}
                style={{ maxHeight: "14rem" }}
              />
            </ComboboxListVirtualized>
          </div>
          {branch.isSelectingWorktreeBase ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <label
                    htmlFor={branch.startFromOriginSwitchId}
                    className="flex cursor-pointer items-center justify-between gap-3 border-t border-border/60 px-3 py-2 text-xs"
                  >
                    <span className="flex min-w-0 items-center gap-1.5 font-medium text-muted-foreground">
                      <RefreshCwIcon aria-hidden="true" className="size-3 shrink-0 opacity-70" />
                      <span className="truncate">Start from origin</span>
                    </span>
                    <Switch
                      id={branch.startFromOriginSwitchId}
                      checked={controller.startFromOrigin}
                      className="[--thumb-size:--spacing(3.5)]"
                      aria-label="Start worktree from origin"
                      onCheckedChange={(checked) =>
                        controller.onStartFromOriginChange(Boolean(checked))
                      }
                    />
                  </label>
                }
              />
              <TooltipPopup side="top" className="max-w-72 whitespace-normal leading-tight">
                Creates the worktree from the latest matching branch on origin instead of your local
                branch.
              </TooltipPopup>
            </Tooltip>
          ) : null}
          {branch.branchStatusText ? (
            <ComboboxStatus>{branch.branchStatusText}</ComboboxStatus>
          ) : null}
        </div>
      </ComboboxPopup>
    </Combobox>
  );
}
